import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { useDriversList } from '@/features/drivers/api';
import { useVehiclesList } from '@/features/vehicles/api';
import { useCreateAssignment } from './api';

type Mode =
  | { kind: 'forVehicle'; vehicleId: string; vehicleLabel: string }
  | { kind: 'forDriver'; driverId: string; driverLabel: string };

interface AssignmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
}

function todayLocalISO(): string {
  // YYYY-MM-DDTHH:mm em horário local — formato aceito por <input type="datetime-local">
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AssignmentFormDialog({ open, onOpenChange, mode }: AssignmentFormDialogProps) {
  const [counterpartyId, setCounterpartyId] = useState<string>('');
  const [startedAt, setStartedAt] = useState<string>(todayLocalISO());
  const [notes, setNotes] = useState<string>('');

  // Buscar a lista contrária ao "lado fixado"
  const driversQuery = useDriversList(
    mode.kind === 'forVehicle' ? { limit: 100, active: 'true', sort: 'name' } : {},
  );
  const vehiclesQuery = useVehiclesList(
    mode.kind === 'forDriver' ? { limit: 100, active: 'true', sort: 'plate' } : {},
  );

  const options = useMemo(() => {
    if (mode.kind === 'forVehicle') {
      return (driversQuery.data?.data ?? []).map((d) => ({
        value: d.id,
        label: `${d.name} · CNH ${d.cnhCategory}`,
      }));
    }
    return (vehiclesQuery.data?.data ?? []).map((v) => ({
      value: v.id,
      label: `${v.plate}${v.brand || v.model ? ` — ${[v.brand, v.model].filter(Boolean).join(' ')}` : ''}`,
    }));
  }, [mode.kind, driversQuery.data, vehiclesQuery.data]);

  const loadingOptions =
    mode.kind === 'forVehicle' ? driversQuery.isLoading : vehiclesQuery.isLoading;

  const create = useCreateAssignment();

  // Reset quando abre/fecha
  useEffect(() => {
    if (open) {
      setCounterpartyId('');
      setStartedAt(todayLocalISO());
      setNotes('');
    }
  }, [open]);

  const canSubmit = counterpartyId.length > 0 && startedAt.length > 0 && !create.isPending;

  const handleSubmit = async () => {
    const payload = {
      driverId: mode.kind === 'forDriver' ? mode.driverId : counterpartyId,
      vehicleId: mode.kind === 'forVehicle' ? mode.vehicleId : counterpartyId,
      // Converte datetime-local → ISO UTC
      startedAt: new Date(startedAt).toISOString(),
      notes: notes.trim() || undefined,
    };
    try {
      await create.mutateAsync(payload);
      toast.success('Vínculo criado');
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const title =
    mode.kind === 'forVehicle' ? 'Vincular motorista ao veículo' : 'Vincular veículo ao motorista';
  const description =
    mode.kind === 'forVehicle'
      ? `Escolha o motorista que assumirá ${mode.vehicleLabel}. Se já houver um vínculo ativo, ele será encerrado automaticamente.`
      : `Escolha o veículo que ${mode.driverLabel} passará a operar. Se já houver outro motorista no veículo, ele será encerrado automaticamente.`;
  const counterpartyLabel = mode.kind === 'forVehicle' ? 'Motorista' : 'Veículo';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="counterparty">{counterpartyLabel}</Label>
            <Select value={counterpartyId} onValueChange={setCounterpartyId}>
              <SelectTrigger id="counterparty">
                <SelectValue
                  placeholder={
                    loadingOptions
                      ? 'Carregando…'
                      : options.length === 0
                        ? `Nenhum ${counterpartyLabel.toLowerCase()} ativo cadastrado`
                        : `Selecione um ${counterpartyLabel.toLowerCase()}`
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="startedAt">Início do vínculo</Label>
            <Input
              id="startedAt"
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Observações (opcional)</Label>
            <Input
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex.: rota A, turno noturno"
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending ? 'Vinculando…' : 'Vincular'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
